<template>
  <div class="col-sm-12" @click.stop="">
    <div class="col-sm-12 editor-form px-4 pt-3">
      <h2 v-if="user" class="editor-header">{{ user.first_name}}  {{ user.last_name}}</h2>
      <div class="row">
        <div class="col-sm-6">
          <span class="span-title">
            {{ $t('certificate.certificate_type') }}
          </span>
          <select
            id="types"
            v-model="certificateUpdate.type"
            class="form-select form-select-sm"
            :disabled="certificateTypes.length < 1"
          >
              <option :value="null">
                {{ $t('certificate.select_type') }}
              </option>
            <option
              v-for="option in certificateTypes"
              :key="option.id"
              :value="option"
            >
              {{ option.name }}
            </option>
          </select>
        </div>
        <div class="col-sm-6">
          <span class="span-title">
            {{ $t('certificate.granted_day') }}
          </span>
          <input
            id="granted_day"
            v-model="certificateUpdate.granted_day"
            class="form-control form-control-sm"
            type="date"
          >
        </div>
      </div>
      <div class="row">
        <div v-if="certificateUpdate.type && certificateUpdate.type.is_expiring" class="col-sm-6">
          <span class="span-title">
            {{ $t('certificate.expiration_day') }}
          </span>
          <input
            id="expiration_day"
            v-model="certificateUpdate.expiration_day"
            class="form-control form-control-sm"
            type="date"
          />
        </div>
        <div class="col-sm-6" :class="{'pt-4' : certificateUpdate.type && certificateUpdate.type.is_expiring}">
            <div class="form-check form-check-md">
                <input
                    class="form-check-input"
                    type="checkbox"
                    id="selectAllContracts"
                    v-model="planned"
                />
                <label class="form-check-label own-check" for="selectAllContracts">
                    {{ $t('certificate.certificate_planned') }}
                </label>
            </div>
        </div>
        </div>

        <div v-if="user === null" class="col-sm-12 my-2" style="min-height: 20em">
              <span class="search-title">{{ $t('certificate.users') }}</span>
              <multiselect
                  v-model="selectedUsers"
                  :placeholder="$t('certificate.select_users')"
                  :deselect-label="$t('common.remove_selection')"
                  :select-label="$t('common.select')"
                  :close-on-select="false"
                  :multiple="true"
                  label="name"
                  track-by="value"
                  :searchable="true"
                  :internal-search="true"
                  :selected-label="$t('common.selected')"
                  :options="resultsUsers"
              >
                  <template
                      #singleLabel="props"
                  >
              <span class="option__desc">
                <span class="option__title">{{ props.option.name }}</span>
              </span>
                  </template>
                  <template
                      #option="props"
                  >
                      <div class="option__desc">
                          <span class="option__title multiselect_detail_block">{{ props.option.name }}</span>
                      </div>
                  </template>
                  <template #noResult>
                      <span>{{ $t('common.not_found') }}</span>
                  </template>
                  <template #noOptions>
                      <span>{{ $t('common.no_results') }}</span>
                  </template>
              </multiselect>
          </div>
        <div class="col-sm-12 my-2 d-flex justify-content-end">
            <button
                class="btn btn-danger form-button"
                @click.stop="$emit('closeAndUpdate')"
            >
              {{ $t('common.cancel') }}
            </button>
          <button
            v-if="certificate.id >= 1 "
            class="btn btn-danger form-button"
            @click.stop="deleteCertificate"
          >
              {{ $t('common.remove') }}
          </button>
          <button
            class="btn btn-success form-button"
            :disabled="!canSubmit"
            @click.stop="submit"
          >
              {{ $t('common.save') }}
          </button>
        </div>
        <div
          v-if="loading"
          id="loader"
          class="spinner"
        />
    </div>
  </div>
</template>

<script>
import {restApi} from '.././mixins/RestApiMixin'
import {timeUtils} from '.././mixins/TimeUtils'
import {inject} from "vue";

export default {
    name: 'CertificateEditor',
    mixins: [restApi, timeUtils],
    props: {
        user: {
            type: Object,
            default: null
        },
        results: {
            type: Array,
            default: function () {
                return []
            }
        },
        certificate: {
            type: Object,
            default: null
        }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    data: function () {
        return {
            loading: false,
            planned: false,
            selectedUsers: [],
            resultsUsers: [],
            certificateTypes: [],
            certificateUpdate: this.formatObj(this.certificate)
        }
    },
    computed: {
        canSubmit: function () {
            if (this.loading || !this.certificateUpdate.type) {
                return false
            }
            if (!this.certificateUpdate.granted_day) {
                return this.planned
            }
            if (!this.certificateUpdate.expiration_day && this.certificateUpdate.type.is_expiring) {
                return false
            }
            return !this.user || this.selectedUsers.length < 1
        }
    },
    watch: {
      planned: function () {
        if (this.planned && !this.certificateUpdate.planned_date) {
            this.certificateUpdate.planned = 1
            this.certificateUpdate.planned_date = this.getDateIsoString(new Date())
        } else {
            this.certificateUpdate.planned = 0
            this.certificateUpdate.planned_date = null
        }
      }
    },
    mounted: function () {
        this.fetchCertificateTypes()
        this.planned = this.certificateUpdate.planned && this.certificateUpdate.planned === true
        //Set up the users to be displayed in the multiselect
        this.resultsUsers = []
        this.results.forEach(user => this.resultsUsers.push({
            value: user.id,
            name: user.last_name + ', ' + user.first_name + ' - ' + user.email
        }), this)
    },
    methods: {
        formatObj: function (object) {
            let obj = JSON.parse(JSON.stringify(object))
            obj.granted_day = this.timestampToDateInput(obj.granted_day)
            obj.expiration_day = obj.expiration_day ? this.timestampToDateInput(obj.expiration_day) : undefined
            return obj
        },
        submit: function () {
            this.loading = true
            if (this.user === null) {
                //Assigning a new certificate to multiple users at once
                //Set certificate id and user to null
                let certificate = JSON.parse(JSON.stringify(this.certificateUpdate))
                certificate.id = null
                certificate.user = null

                //Create array of user ids
                let userIds = []
                this.selectedUsers.forEach(function (item) {
                    userIds.push(item.value)
                })
                //Create DTO containing certificate and users array
                let dto = {}
                dto.certificate = certificate
                dto.users = userIds
                this.restAdd(this.certificateManyUrl, dto, this.success, this.fail)
            } else {
                if (this.certificateUpdate.id < 1) {
                    //Assigning a new certificate to one user
                    let certificate = JSON.parse(JSON.stringify(this.certificateUpdate))
                    certificate.id = null
                    this.restAdd(this.certificateUrl, certificate, this.success, this.fail)
                } else {
                    //Updating one user's certificate
                    this.restUpdate(this.certificateUrl, this.certificateUpdate, this.success, this.fail)
                }
            }
        },
        success: function () {
            this.$emit('closeAndUpdate')
            this.loading = false
        },
        fail: function () {
            this.showAlert(this.$t('common.alert_update_failed'))
            this.loading = false
        },
        deleteCertificate: function () {
            this.loading = true
            this.restDelete(this.certificateUrl + '?id=' + this.certificateUpdate.id, this.success, this.fail)
        },
        fetchCertificateTypes: function () {
            this.loading = true
            this.restFetch(this.certificateTypeUrl, this.handleTypeResponse)
        },
        handleTypeResponse: function (response) {
            this.certificateTypes = response.data
            this.loading = false
        }
    }
}
</script>
